/**
 * ---------------------------------------------------------------------------------------------------------------------
 * BEFORE & AFTER GRID
 * ---------------------------------------------------------------------------------------------------------------------
 * The comparison slider on each card of the "Before & after grid" section.
 *
 * Everything the card draws hangs off one custom property, --ba-position: it clips the after image and places the
 * handle. Dragging is therefore just a matter of writing that property.
 *
 * Two input paths feed the same value. Pointer events are taken on the card, so a shopper can grab anywhere on the
 * image rather than having to hit the handle exactly. The range input behind it holds the value for keyboard and
 * screen reader users, who get arrow keys, Home and End from the browser for free. Whichever moves, both end up
 * carrying the same value.
 */

(function () {
  'use strict';

  class BeforeAfterCompare extends HTMLElement {
    connectedCallback() {
      this.range = this.querySelector('[data-ba-range]');

      if (!this.range) return;

      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.range.addEventListener('input', () => this.setPosition(this.range.value), { signal });

      this.addEventListener('pointerdown', (event) => this.onPointerDown(event), { signal });
      this.addEventListener('pointermove', (event) => this.onPointerMove(event), { signal });
      this.addEventListener('pointerup', (event) => this.onPointerUp(event), { signal });
      this.addEventListener('pointercancel', (event) => this.onPointerUp(event), { signal });

      // Only claim the drag cursor once the card can actually be dragged
      this.classList.add('ba-card--interactive');
      this.setPosition(this.range.value);
    }

    disconnectedCallback() {
      if (this.abortController) this.abortController.abort();
    }

    onPointerDown(event) {
      this.activePointer = event.pointerId;
      this.setPointerCapture(event.pointerId);

      // Stops the browser starting its own image drag from under the pointer
      event.preventDefault();

      this.positionFromPointer(event);
    }

    onPointerMove(event) {
      if (this.activePointer !== event.pointerId) return;

      this.positionFromPointer(event);
    }

    onPointerUp(event) {
      if (this.activePointer !== event.pointerId) return;

      this.activePointer = null;

      if (this.hasPointerCapture(event.pointerId)) this.releasePointerCapture(event.pointerId);
    }

    positionFromPointer(event) {
      const rect = this.getBoundingClientRect();

      if (!rect.width) return;

      const ratio = ((event.clientX - rect.left) / rect.width) * 100;
      const value = Math.min(100, Math.max(0, ratio));

      // Writing the input back keeps the keyboard slider carrying on from wherever the drag left off
      this.range.value = value;
      this.setPosition(value);
    }

    setPosition(value) {
      this.style.setProperty('--ba-position', `${value}%`);
    }
  }

  if (!window.customElements.get('ba-compare')) {
    window.customElements.define('ba-compare', BeforeAfterCompare);
  }
})();
